import { action, computed, makeAutoObservable, observable, runInAction } from 'mobx';
import toast from 'react-hot-toast';
import URLConstants from '../../constants/url-constants';
import {
  normalizeDimensionsByColumn,
  transformDimensionData,
} from '../../helpers/array-to-tree-helper';
import config from '../../helpers/config-helper';
import { getTenantIdFromLocalStorage } from '../../helpers/localstorage-helper';
import { ICopyProduct } from '../../models/forms/ICopyProduct';
import { IOption, IReactSelectOption } from '../../models/ICommon';
import IApiResponse, {
  IApiSuccessResponse,
} from '../../models/response/IApiResponse';
import { IProcessDimensionData } from '../../models/response/IProcessDimensionData';
import {
  IAddProductFileResponse,
  IGetDimensionColumnResponse,
  IProductFiles,
  IProductImages,
  IProductList,
  IProductListVM,
  IProductOptions,
  IProductVM,
} from '../../models/response/IProductResponse';
import { formatMessage } from '../../translations/format-message';
import {
  mergeUniqueProductFiles,
} from '../../helpers/product-file-delete-helper';
import {
  GetAllProducts,
  GetProductState,
  ObservableInitialState,
} from '../initialState';
import * as baseService from '../service/base-service';
const appConfig = config();

export class ProductStore {
  initialStateValue = ObservableInitialState;
  productList: IProductListVM = GetAllProducts;
  product: IProductVM | undefined = undefined;
  copyProductImages: IProductFiles[] = [];
  productState = { ...this.initialStateValue };
  addUpdateProductState = { ...this.initialStateValue };
  deleteProductState = { ...this.initialStateValue };
  getAllProductState = { ...this.initialStateValue };
  addProductFileState = { ...this.initialStateValue };
  deleteProductFileState = { ...this.initialStateValue };
  getDimensionColumnState = { ...this.initialStateValue };
  exportProductReportState = { ...this.initialStateValue };
  dimensionTableColumnKeys: IGetDimensionColumnResponse[] = [];
  productImages: IProductFiles[] | undefined = undefined;
  productOptions: IProductOptions[] = [];
  copyProductState = { ...this.initialStateValue };
  newCopiedProductId: number | null = null;
  ValidateProductNameState = { ...this.initialStateValue };
  getDimensionByProcessIdState = { ...this.initialStateValue };
  isProductNameValid: boolean = false;
  copiedImagesUrl: Array<IProductFiles> = [];
  productId: string = '';
  processDimension: IProcessDimensionData[] | undefined = undefined;
  dimensionOrder: number[] = [];
  oldDeletedFiles: Array<IProductFiles> = [];
  newDeletedFiles: Array<IProductFiles> = [];
  newAccidentalDeletedFiles: Array<IProductFiles> = [];
  base64String: any = { fileString: '' };

  constructor() {
    makeAutoObservable(this, {
      product: observable,
      oldDeletedFiles: observable,
      newDeletedFiles: observable,
      newAccidentalDeletedFiles: observable,
      productState: observable,
      productList: observable,
      addUpdateProductState: observable,
      deleteProductState: observable,
      getAllProductState: observable,
      deleteProductFileState: observable,
      productId: observable,
      copiedImagesUrl: observable,
      copyProductImages: observable,
      getDimensionColumnState: observable,
      dimensionTableColumnKeys: observable,
      processDimension: observable,
      setCopiedImagesUrl: action,
      setNewDeletedFiles: action,
      setOldDeletedFiles: action,
      setNewAccidentalDeletedFiles: action,
      resetNewAccidentalDeletedFiles: action,
      GetDimensionByProcessIdService: action,
      GetDimensionColumnService: action,
      resetCopiedImagesUrl: action,
      GetProductListService: action,
      GetProductByIdService: action,
      GetProductImagesByIdService: action,
      AddProductService: action,
      UpdateProductService: action,
      DeleteProductService: action,
      GetAllProductService: action,
      UploadProductFileService: action,
      DeleteProductFileService: action,
      UploadFileCopyService: action,
      resetDeleteProduct: action,
      resetAddUpdateProductState: action,
      reset: action,
      resetAddProductFileState: action,
      resetDeleteProductFileState: action,
      setProductId: action,
      getproductId: computed,
      getDimensionTableColumnKeys: computed,
      allProduct: computed,
      getCopiedImagesUrl: computed,
      productDetails: computed,
      allProductImages: computed,
      productImages: observable,
      addProductFileState: observable,
      resetProductImages: action,
      allProductOptions: computed,
      productOptions: observable,
      allProductProcessTemplates: computed,
      CopyProductService: action,
      copyProductState: observable,
      resetCopyProductState: action,
      newCopiedProductId: observable,
      ValidateProductNameState: observable,
      resetValidateProductNameState: action,
      isProductNameValid: observable,
      setCopyProductDetails: action,
      allProductReactSelectOptions: computed,
      allProductSearchSelectOptions: computed,
      getDimensionByProcessId: computed,
      getDimensionOrder: computed,
      getNewAccidentalDeletedFiles: computed,
      setIsProductNameValid: action,
      exportProductReportState: observable,
      ExportProductReportService: action,
      base64String: observable,
      resetExportProductReportState: action,
    });
  }

  //#region Actions
  buildHeaderAlignedDimensionValues = (
    selectedColumns: any[] = [],
    tableData: any[] = [],
    targetColumnLabel: string
  ) => {
    const headers = (selectedColumns || [])
      .map((col: any) => col?.label)
      .filter((label: any) => typeof label === 'string' && label.trim() !== '');

    const targetIndex = headers.findIndex(
      (label: string) => label === targetColumnLabel
    );
    if (targetIndex === -1) return [];

    // Preserve UI row order/count: one entry per TableData row.
    // Use "-" placeholder for empty cells so backend and UI can keep alignment.
    return (tableData || []).map((row: any) => {
      const value = row?.[targetColumnLabel];
      return value === undefined || value === null || String(value).trim() === ''
        ? '-'
        : String(value);
    });
  };

  buildRootDimensionsData = (data: any) => {
    const selectedColumns = this.dedupeDimensionColumns(
      data?.Dimension?.SelectedColumns || []
    );
    const tableData = data?.Dimension?.TableData || [];

    const mappedFromDimension =
      Array.isArray(selectedColumns) && selectedColumns.length > 0
        ? selectedColumns
          .filter((s: any) => Number(s?.id || 0) > 0 && String(s?.label || '').trim() !== '')
          .map((s: any) => ({
            DimensionId: Number(s?.id || 0),
            DimensionColumn: String(s?.label || '').trim(),
            DimensionType: Number(s?.data?.MessagesType || 0),
            DimensionValues: this.buildHeaderAlignedDimensionValues(
              selectedColumns,
              tableData,
              s?.label
            ),
          }))
        : [];

    if (mappedFromDimension.length > 0) {
      return mappedFromDimension;
    }

    const fallback = Array.isArray(data?.DimensionsData) ? data.DimensionsData : [];
    return fallback.map((d: any) => ({
      DimensionId: Number(d?.DimensionId || 0),
      DimensionColumn: String(d?.DimensionColumn || '').trim(),
      DimensionType: Number(d?.DimensionType || 0),
      DimensionValues: Array.isArray(d?.DimensionValues)
        ? d.DimensionValues.map((v: any) => String(v ?? '-'))
        : [],
    }));
  };

  isTeplerProcessType = (type: any, processName?: any, shortName?: any) => {
    // backend expects IsPrint=true whenever product has a print/tepler process.
    // Keep this detection tolerant to numeric type + legacy/misspelled labels.
    const numericType = Number(type);
    if (!Number.isNaN(numericType) && numericType === 2) {
      return true;
    }

    const texts = [type, processName, shortName]
      .map((value: any) => String(value ?? '').trim().toLowerCase())
      .filter((value: string) => value.length > 0);

    return texts.some(
      (value: string) =>
        value === 'print' ||
        value.includes('tepler') ||
        value.includes('teplar') ||
        value.includes('tapler')
    );
  };

  hasTeplerProcess = (processes: any[] = []) => {
    // Centralized guard used by both Add/Update payload builders so IsPrint
    // cannot remain false when print/tepler exists in Process array.
    return (processes || []).some((process: any) =>
      this.isTeplerProcessType(
        process?.Type,
        process?.ProcessName,
        process?.ShortName
      )
    );
  };

//  isTeplerProcessType = (type: any, processName?: any, shortName?: any) => {
//     // backend expects IsPrint=true whenever product has a print/tepler process.
//     // Keep this detection tolerant to numeric type + legacy/misspelled labels.
//     const numericType = Number(type);
//     if (!Number.isNaN(numericType) && numericType === 2) {
//       return true;
//     }
//     const texts = [type, processName, shortName]
//       .map((value: any) => String(value ?? '').trim().toLowerCase())
//       .filter((value: string) => value.length > 0);
//     return texts.some(
//       (value: string) =>
//         value === 'print' ||
//         value.includes('tepler') ||
//         value.includes('teplar') ||
//         value.includes('tapler')
//     );
//   };
//   hasTeplerProcess = (processes: any[] = []) => {
//     // Centralized guard used by both Add/Update payload builders so IsPrint
//     // cannot remain false when print/tepler exists in Process array.
//     return (processes || []).some((process: any) =>
//       this.isTeplerProcessType(
//         process?.Type,
//         process?.ProcessName,
//         process?.ShortName
//       )
//     );
//   };

  normalizeFileIndicesForPayload = (fileIndices: any[] = []) => {
    const isTrue = (val: any) => val === true || String(val).toLowerCase() === 'true';

    const normalized = (fileIndices || []).map((f: any) => ({
      ...f,
      IsDefault: isTrue(f?.IsDefault) || isTrue(f?.isDefault),
      IsDimension: isTrue(f?.IsDimension) || isTrue(f?.isDimension),
    }));

    const dimensionTabs = normalized.filter((f: any) => f?.IsDimension);
    const commonTabs = normalized.filter(
      (f: any) => !f?.IsDimension && f?.IsDefault
    );
    const regularTabs = normalized.filter(
      (f: any) => !f?.IsDimension && !f?.IsDefault
    );

    const ordered = [...dimensionTabs, ...commonTabs, ...regularTabs];

    return ordered.map((f: any, idx: number) => ({
      ...f,
      TabIndex: idx + 1,
    }));
  };

  dedupeDimensionColumns = (columns: any[] = []) => {
    const seen = new Set<string>();
    const dedupedColumns = (columns || []).filter((col: any) => {
      const id = Number(col?.id || 0);
      const label = String(col?.label || '').trim().toLowerCase();
      const key = `${id}__${label}`;
      if (seen.has(key)) return false;
      seen.add(key);
      return id > 0 && label !== '';
    });

    const masterOrder = Array.isArray(this.dimensionTableColumnKeys)
      ? this.dimensionTableColumnKeys
        .map((col: any) => Number(col?.Id || 0))
        .filter((id: number) => id > 0)
      : [];
    const order = masterOrder.length > 0 ? masterOrder : this.dimensionOrder || [];
    if (!order || order.length === 0) return dedupedColumns;

    return [...dedupedColumns].sort((a: any, b: any) => {
      const indexA = order.indexOf(Number(a?.id || a?.value || a?.data?.Id || 0));
      const indexB = order.indexOf(Number(b?.id || b?.value || b?.data?.Id || 0));

      if (indexA === -1 && indexB === -1) return 0;
      if (indexA === -1) return 1;
      if (indexB === -1) return -1;
      return indexA - indexB;
    });
  };

//  isTeplerProcessType = (type: any, processName?: any, shortName?: any) => {
//     // backend expects IsPrint=true whenever product has a print/tepler process.
//     // Keep this detection tolerant to numeric type + legacy/misspelled labels.
//     const numericType = Number(type);
//     if (!Number.isNaN(numericType) && numericType === 2) {
//       return true;
//     }
//     const texts = [type, processName, shortName]
//       .map((value: any) => String(value ?? '').trim().toLowerCase())
//       .filter((value: string) => value.length > 0);
//     return texts.some(
//       (value: string) =>
//         value === 'print' ||
//         value.includes('tepler') ||
//         value.includes('teplar') ||
//         value.includes('tapler')
//     );
//   };
//   hasTeplerProcess = (processes: any[] = []) => {
//     // Centralized guard used by both Add/Update payload builders so IsPrint
//     // cannot remain false when print/tepler exists in Process array.
//     return (processes || []).some((process: any) =>
//       this.isTeplerProcessType(
//         process?.Type,
//         process?.ProcessName,
//         process?.ShortName
//       )
//     );
//   };

  // normalizeFileIndicesForPayload = (fileIndices: any[] = []) => {
  //   const isTrue = (val: any) => val === true || String(val).toLowerCase() === 'true';

  //   const normalized = (fileIndices || []).map((f: any) => ({
  //     ...f,
  //     IsDefault: isTrue(f?.IsDefault) || isTrue(f?.isDefault),
  //     IsDimension: isTrue(f?.IsDimension) || isTrue(f?.isDimension),
  //   }));

  //   const dimensionTabs = normalized.filter((f: any) => f?.IsDimension);
  //   const commonTabs = normalized.filter(
  //     (f: any) => !f?.IsDimension && f?.IsDefault
  //   );
  //   const regularTabs = normalized.filter(
  //     (f: any) => !f?.IsDimension && !f?.IsDefault
  //   );

  //   const ordered = [...dimensionTabs, ...commonTabs, ...regularTabs];

  //   return ordered.map((f: any, idx: number) => ({
  //     ...f,
  //     TabIndex: idx + 1,
  //   }));
  // };

  // dedupeDimensionColumns = (columns: any[] = []) => {
  //   const seen = new Set<string>();
  //   return (columns || []).filter((col: any) => {
  //     const id = Number(col?.id || 0);
  //     const label = String(col?.label || '').trim().toLowerCase();
  //     const key = `${id}__${label}`;
  //     if (seen.has(key)) return false;
  //     seen.add(key);
  //     return id > 0 && label !== '';
  //   });
  // };

  GetProductListService = (
    currentPage: number,
    pagerSize: number,
    tenantId: number,
    filters: string
  ) => {
    this.getAllProductState.inProgress = true;
    const url = `${URLConstants.GetAllProductList}?TenantId=${tenantId}&PageSize=${pagerSize}&PageNo=${currentPage}&Filters=${filters}`;
    return baseService
      .getRequest(url)
      .then((response: IApiResponse<IApiSuccessResponse<IProductListVM>>) => {
        if (response.data.Error) {
          this.getAllProductState.error = response.data.Message;
          toast.error(formatMessage(response.data.Message));
        } else {
          this.productList = response.data.Data;
          this.getAllProductState.success = true;
        }
      })
      .catch((err: string) => {
        this.getAllProductState.error = err;
      })
      .finally(
        action(() => {
          this.getAllProductState.inProgress = false;
        })
      );
  };

  AddProductService = (data: any) => {
    const finalJson = {
      TenantId:
        Number(data.TenantId) > 0
          ? Number(data.TenantId)
          : Number(getTenantIdFromLocalStorage() || 0),
      Id: Number(data.Id || 0),
      ProductName: data.ProductName || '',
      Katashiki: data.Katashiki || '',
      Customer: data.Customer || '',
      TraySize: data.TraySize || '',
      KeyInfo1: data.KeyInfo1 || '',
      KeyInfo2: data.KeyInfo2 || '',
      KeyInfo3: data.KeyInfo3 || '',
      Remarks: data.Remarks || '',
      Process: (data.Process || [])
        .filter((p: any) => p.ProcessId !== -3 && p.ProcessId !== -4)
        .map((p: any) => {
          const isSystemProcess = Number(p.ProcessId) < 0;
          const normalizedFileIndices = isSystemProcess ? [] : this.normalizeFileIndicesForPayload(
            p.fileIndices || []
          );
          return {
            ProcessId: p.ProcessId || 0,
            ProcessName: p.ProcessName || '',
            ShortName: p.ShortName || '',
            Type: p.Type || 0,
            DueDates: p.DueDates || 0,
            ProcessOrder: p.ProcessOrder || 0,
            StandardTime: p.StandardTime || '',
            ViewType: p.ViewType || 0,
            FileDeatil: normalizedFileIndices.map((f: any) => {
            const headerMap = new Map();
            (f.CheckItems || []).forEach((item: any) => {
              const messages = item.CheckItems && Array.isArray(item.CheckItems)
                ? item.CheckItems
                : (item.CheckItemMessage && Array.isArray(item.CheckItemMessage)
                  ? item.CheckItemMessage
                  : [item]);
              const title = (item.HeaderTitle || item.Title || 'Check Item').trim();

              messages.forEach((msg: any) => {
                const text = typeof msg === 'string'
                  ? msg
                  : (msg.CheckMessage || msg.text || (typeof msg.CheckItemMessage === 'string' ? msg.CheckItemMessage : ''));

                if (typeof text === 'string' && text.trim() !== '') {
                  if (!headerMap.has(title)) {
                    headerMap.set(title, {
                      Title: title,
                      Order: headerMap.size,
                      CheckItems: []
                    });
                  }
                  headerMap.get(title).CheckItems.push({
                    Id: msg.Id || msg.apiId || 0,
                    CheckMessage: text.trim(),
                    IsChecked: !!(msg.IsChecked || msg.isInfo)
                  });
                }
              });
            });

            return {
              TabIndex: f.TabIndex || 0,
              IsDefault: !!f.IsDefault,
              IsDimension: !!f.IsDimension,
              FileDetail: (f.FileDetail || []).map((fd: any) => ({
                FileId: fd.FileId && Number(fd.FileId) > 0 ? Number(fd.FileId) : (f.IsNew || fd.IsNew ? 0 : fd.FileId || 0),
                Heading: fd.Heading || '',
                FileName: fd.FileName || '',
                FileType: fd.FileType || '',
                S3FilePath: fd.S3FilePath || '',
                FilePath: fd.FilePath || ''
              })),
              Headers: Array.from(headerMap.values()),
              DimensionsData: f?.IsDimension && p?.DimensionDetails?.SelectedColumns?.length > 0 && p?.DimensionDetails?.TableData?.length > 0
                ? this.dedupeDimensionColumns(
                  p?.DimensionDetails?.SelectedColumns || []
                ).map((s: any) => ({
                  DimensionId: s?.id,
                  DimensionColumn: s?.label,
                  DimensionType: s?.data?.MessagesType || 0,
                  DimensionValues: this.buildHeaderAlignedDimensionValues(
                    this.dedupeDimensionColumns(
                      p?.DimensionDetails?.SelectedColumns || []
                    ),
                    p?.DimensionDetails?.TableData || [],
                    s?.label
                  ),
                }))
                : []
            };
          }),
        };
      }),
      ProductTeplars: (data.ProductTeplars || []).map((t: any, index: number) => ({
        Id: t.Id || 0,
        Order: Number(t.Order || index + 1),
        Format: t.Format || '',
        Layout: t.Layout || 0,
        Width: t.Width || 0,
        Items: t.Items || '',
        Position: t.Position || '',
        Remarks: t.Remarks || ''
      })),
      DimensionsData: this.buildRootDimensionsData(data),
      // Force true for print/tepler process to prevent false payload from UI state.
      IsPrint: this.hasTeplerProcess(data.Process || []) ? true : this.hasTeplerProcess(data.Process || []) ? true : !!data.IsPrint,
      IsImport: !!data.IsImport
    };
    // console.log('AddProductService Final Payload:', finalJson);
    this.addUpdateProductState.inProgress = true;
    const url = URLConstants.AddProduct;

    return baseService
      .postRequest(url, finalJson)
      .then((response: IApiResponse<IApiSuccessResponse<boolean>>) => {
        if (response.data.Error) {
          this.addUpdateProductState.error = response.data.Message;
        } else {
          this.addUpdateProductState.success = true;
          // 🔥 VERY IMPORTANT FIX
          runInAction(() => {
            this.product?.Process?.forEach((p: any) => {
              p.fileIndices?.forEach((fi: any) => {
                // reset tab flag
                fi.IsNew = false;
                // reset file flags
                fi.FileDetail?.forEach((f: any) => {
                  f.IsNew = false;
                });
              });
            });
          });
          // console.log('[STORE] AddProductService success. oldDeletedFiles in store:', this.oldDeletedFiles.map(f => f.FileId));
          if (this.newDeletedFiles.length > 0 || this.oldDeletedFiles.length > 0) {
            // console.log('[STORE] Triggering DeleteProductFileService...');
            this.DeleteProductFileService(Number(getTenantIdFromLocalStorage() || -1), Number(data.Id || 0), this.newDeletedFiles, this.oldDeletedFiles);
          }
          this.setNewDeletedFiles([]);
          this.setOldDeletedFiles([]);
          this.resetNewAccidentalDeletedFiles();
        }
      })
      .catch((err: string) => {
        this.addUpdateProductState.error = err;
      })
      .finally(
        action(() => {
          this.addUpdateProductState.inProgress = false;
        })
      );
  };

  UpdateProductService = (data: any) => {
    this.addUpdateProductState.inProgress = true;
    let url = URLConstants.UpdateProduct;
    const isTemplateDrivenUpdate = Number(data?.ProcessTemplate || -1) > 0;
    const existingProcessIds = new Set<number>(
      (this.product?.Process || [])
        .map((p: any) => Number(p?.ProcessId || 0))
        .filter((id: number) => id > 0)
    );
    const finalJson = {
      TenantId: Number(data.TenantId || getTenantIdFromLocalStorage() || 0),
      Id: Number(data.Id || 0),
      ProductName: data.ProductName || '',
      Katashiki: data.Katashiki || '',
      Customer: data.Customer || '',
      TraySize: data.TraySize || '',
      KeyInfo1: data.KeyInfo1 || '',
      KeyInfo2: data.KeyInfo2 || '',
      KeyInfo3: data.KeyInfo3 || '',
      Remarks: data.Remarks || '',
      Process: (data.Process || [])
        .filter((p: any) => p.ProcessId !== -3 && p.ProcessId !== -4)
        .map((p: any) => {
          const isSystemProcess = Number(p.ProcessId) < 0;
          const normalizedFileIndices = isSystemProcess ? [] : this.normalizeFileIndicesForPayload(
            p.fileIndices || []
          );
          const isNewlyAddedProcess =
            Number(p?.ProcessId || 0) > 0 &&
            !existingProcessIds.has(Number(p?.ProcessId || 0));
          return {
            ProcessId: p.ProcessId || 0,
            ProcessName: p.ProcessName || '',
            ShortName: p.ShortName || '',
            Type: p.Type || 0,
            DueDates: p.DueDates || 0,
            ProcessOrder: p.ProcessOrder || 0,
            StandardTime: p.StandardTime || '',
            ViewType: p.ViewType || 0,
            FileDeatil: normalizedFileIndices.map((f: any) => {
            const headerMap = new Map();
            (f.CheckItems || []).forEach((item: any) => {
              const messages = item.CheckItems && Array.isArray(item.CheckItems)
                ? item.CheckItems
                : (item.CheckItemMessage && Array.isArray(item.CheckItemMessage)
                  ? item.CheckItemMessage
                  : [item]);
              const title = (item.HeaderTitle || item.Title || 'Check Item').trim();

              messages.forEach((msg: any) => {
                const text = typeof msg === 'string'
                  ? msg
                  : (msg.CheckMessage || msg.text || (typeof msg.CheckItemMessage === 'string' ? msg.CheckItemMessage : ''));

                if (typeof text === 'string' && text.trim() !== '') {
                  if (!headerMap.has(title)) {
                    headerMap.set(title, {
                      Title: title,
                      Order: headerMap.size,
                      CheckItems: []
                    });
                  }
                  headerMap.get(title).CheckItems.push({
                    Id: msg.Id || msg.apiId || 0,
                    CheckMessage: text.trim(),
                    IsChecked: !!(msg.IsChecked || msg.isInfo)
                  });
                }
              });
            });

            return {
              TabIndex: f.TabIndex || 0,
              IsDefault: !!f.IsDefault,
              IsDimension: !!f.IsDimension,
              FileDetail: (f.FileDetail || []).map((fd: any) => ({
                FileId: isTemplateDrivenUpdate || isNewlyAddedProcess
                  ? 0
                  : f.IsNew || fd.IsNew
                      ? 0
                      : fd.FileId && Number(fd.FileId) > 0
                        ? Number(fd.FileId)
                        : fd.FileId || 0,
                Heading: fd.Heading || '',
                FileName: fd.FileName || '',
                FileType: fd.FileType || '',
                S3FilePath: fd.S3FilePath || '',
                FilePath: fd.FilePath || ''
              })),
              Headers: Array.from(headerMap.values()),
              DimensionsData: f?.IsDimension && p?.DimensionDetails?.SelectedColumns?.length > 0 && p?.DimensionDetails?.TableData?.length > 0
                ? this.dedupeDimensionColumns(
                  p?.DimensionDetails?.SelectedColumns || []
                ).map((s: any) => ({
                  DimensionId: s?.id,
                  DimensionColumn: s?.label,
                  DimensionType: s?.data?.MessagesType || 0,
                  DimensionValues: this.buildHeaderAlignedDimensionValues(
                    this.dedupeDimensionColumns(
                      p?.DimensionDetails?.SelectedColumns || []
                    ),
                    p?.DimensionDetails?.TableData || [],
                    s?.label
                  ),
                }))
                : []
            };
          }),
        };
      }),
      ProductTeplars: (data.ProductTeplars || []).map((t: any, index: number) => ({
        Id: t.Id || 0,
        Order: Number(t.Order || index + 1),
        Format: t.Format || '',
        Layout: t.Layout || 0,
        Width: t.Width || 0,
        Items: t.Items || '',
        Position: t.Position || '',
        Remarks: t.Remarks || ''
      })),
      DimensionsData: this.buildRootDimensionsData(data),
      // Same enforcement for update flow (client reported mismatch on update too).
      IsPrint: this.hasTeplerProcess(data.Process || []) ? true : this.hasTeplerProcess(data.Process || []) ? true : !!data.IsPrint,
      IsImport: !!data.IsImport
    };
    // console.log('UpdateProductService Final Payload:', finalJson);
    return baseService
      .putRequest(url, finalJson)
      .then((response: IApiResponse<IApiSuccessResponse<boolean>>) => {
        if (response.data.Error) {
          console.error('[STORE] UpdateProductService API Error:', response.data.Message);
          this.addUpdateProductState.error = response.data.Message;
        } else {
          this.addUpdateProductState.success = true;
          // 🔥 VERY IMPORTANT FIX
          runInAction(() => {
            this.product?.Process?.forEach((p: any) => {
              p.fileIndices?.forEach((fi: any) => {
                // reset tab flag
                fi.IsNew = false;
                // reset file flags
                fi.FileDetail?.forEach((f: any) => {
                  f.IsNew = false;
                });
              });
            });
          });
          // console.log('[STORE] UpdateProductService success. oldDeletedFiles in store:', this.oldDeletedFiles.map(f => f.FileId));
          if (this.newDeletedFiles.length > 0 || this.oldDeletedFiles.length > 0) {
            // console.log('[STORE] Triggering DeleteProductFileService...');
            this.DeleteProductFileService(Number(getTenantIdFromLocalStorage() || -1), Number(data.Id || 0), this.newDeletedFiles, this.oldDeletedFiles);
          }
          this.setNewDeletedFiles([]);
          this.setOldDeletedFiles([]);
          this.resetNewAccidentalDeletedFiles();
        }
      })
      .catch((err: string) => {
        this.addUpdateProductState.error = err;
      })
      .finally(
        action(() => {
          this.addUpdateProductState.inProgress = false;
        })
      );
  };

  GetProductByIdService = (
    tenantId: number,
    productId: number,
    isCopy = false
  ) => {
    this.productState.inProgress = true;
    const url = `${URLConstants.GetProductById}?TenantId=${tenantId}&ProductId=${productId}`;
    return baseService
      .getRequest(url)
      .then((response: IApiResponse<IApiSuccessResponse<any>>) => {
        if (response.data.Error) {
          this.productState.error = response.data.Message;
        } else {
          localStorage.removeItem('images');

          // BACKWARD MAPPING: From New Payload Structure to UI-Friendly Structure
          const rawData = response.data.Data;
          if (rawData) {
            const processedData = {
              ...rawData,
              Process: (rawData.Process || []).map((p: any) => ({
                ...p,
                fileIndices: (p.FileDeatil || p.FileDetail || p.fileIndices || []).map((tab: any) => {
                  const items = (tab.Headers || []).flatMap((h: any) =>
                    (h.CheckItems || []).map((item: any) => ({
                      ...item,
                      HeaderTitle: h.Title,
                    }))
                  );
                  // If Headers is missing but CheckItems is present at tab level, preserve it
                  const initialCheckItems = items.length > 0 ? items : (tab.CheckItems || []);

                  // Normalize FileDetail to use consistent uppercase keys (S3FilePath, FilePath)
                  const rawFileDetail = tab.FileDetail || tab.FileDeatil || [];
                  const normalizedFileDetail = (Array.isArray(rawFileDetail) ? rawFileDetail : []).map((fd: any) => ({
                    ...fd,
                    FileId: fd.FileId ?? fd.fileId ?? 0,
                    Heading: fd.Heading ?? fd.heading ?? '',
                    FileName: fd.FileName ?? fd.fileName ?? '',
                    FileType: fd.FileType ?? fd.fileType ?? '',
                    S3FilePath: fd.S3FilePath ?? fd.s3FilePath ?? '',
                    FilePath: fd.FilePath ?? fd.filePath ?? '',
                  }));

                  return {
                    ...tab,
                    TabIndex: Number(tab?.TabIndex || 0),
                    CheckItems: initialCheckItems,
                    FileDetail: normalizedFileDetail,
                  };
                })
                  .sort((a: any, b: any) => Number(a?.TabIndex || 0) - Number(b?.TabIndex || 0)),
              })),
            };

            // RE-CREATE VIRTUAL PROCESSES (-3 for Common, -4 for Dimension Template)
            const realProcesses = processedData.Process || [];
            const commonTabs: any[] = [];
            const seenCommonHeadings = new Set();
            let dimensionTemplateTab: any = null;

            // Use the FIRST real process's common tabs as the authoritative source.
            // This prevents heading-based deduplication from picking up wrong tab data
            // when tab headings are renamed and overlap with previously used names.
            const firstRealProcess = realProcesses.find((p: any) => Number(p?.ProcessId) > 0);
            if (firstRealProcess) {
              (firstRealProcess.fileIndices || []).forEach((fi: any) => {
                const isTrue = (val: any) => val === true || String(val).toLowerCase() === 'true';
                if (isTrue(fi.IsDefault) || isTrue(fi.isDefault)) {
                  commonTabs.push({ ...fi });
                }
                if (isTrue(fi.IsDimension) || isTrue(fi.isDimension)) {
                  if (!dimensionTemplateTab) {
                    dimensionTemplateTab = { ...fi };
                  }
                }
              });
            } else {
              // Fallback: no real process found, collect from all (original behavior)
              realProcesses.forEach((p: any) => {
                (p.fileIndices || []).forEach((fi: any) => {
                  const isTrue = (val: any) => val === true || String(val).toLowerCase() === 'true';
                  if (isTrue(fi.IsDefault) || isTrue(fi.isDefault)) {
                    const heading = fi.FileDetail?.[0]?.Heading;
                    if (heading && !seenCommonHeadings.has(heading)) {
                      commonTabs.push({ ...fi });
                      seenCommonHeadings.add(heading);
                    }
                  }
                  if (isTrue(fi.IsDimension) || isTrue(fi.isDimension)) {
                    if (!dimensionTemplateTab) {
                      dimensionTemplateTab = { ...fi };
                    }
                  }
                });
              });
            }

            const virtualProcess3 = {
              ProcessId: -3,
              ProcessName: 'Common Process',
              fileIndices: commonTabs,
            };

            // Crucial: UI expects CommonProcess at root
            processedData.CommonProcess = commonTabs;

            processedData.Process = [virtualProcess3, ...realProcesses];

            if (dimensionTemplateTab) {
              const virtualProcess4 = {
                ProcessId: -4,
                ProcessName: 'Dimension Template',
                fileIndices: [dimensionTemplateTab],
              };
              processedData.Process.push(virtualProcess4);
            }

            if (!isCopy) {
              this.product = processedData;
            } else {
              this.product = {
                ...processedData,
                ProductName: '',
                CommonProcess: [],
                ProductFiles: [],
                ProductFilesInfo: [],
                ProductTeplars: [],
                DimensionsData: [],
                Dimension: {
                  SelectedColumns: [],
                  TableData: [],
                },
                SelectedProcessForDimensionTable: -1,
                Process: (processedData.Process || []).map((p: any) => ({
                  ...p,
                  fileIndices: [],
                })),
              };
            }
          }
          this.productState.success = true;
        }
      })
      .catch((err: string) => {
        this.productState.error = err;
      })
      .finally(
        action(() => {
          this.productState.inProgress = false;
        })
      );
  };

  GetProductImagesByIdService = (
    tenantId: number,
    productId: number,
    isCopy = false
  ) => {
    this.productState.inProgress = true;
    this.copyProductImages = [];
    this.setCopiedImagesUrl([]);
    const url = `${URLConstants.GetProductImageById}/${tenantId}?ProductId=${productId}`;
    return baseService
      .getRequest(url)
      .then((response: IApiResponse<IApiSuccessResponse<any>>) => {
        if (response.data.Error) {
          this.productState.error = response.data.Message;
          this.copyProductImages = [];
          this.setCopiedImagesUrl([]);
        } else {
          const images = Array.isArray(response.data.Data)
            ? response.data.Data
            : [];
          this.copyProductImages = images;
          this.setCopiedImagesUrl(this.copyProductImages);
          this.productState.success = true;
        }
      })
      .catch((err: string) => {
        this.productState.error = err;
        this.copyProductImages = [];
        this.setCopiedImagesUrl([]);
      })
      .finally(
        action(() => {
          this.productState.inProgress = false;
        })
      );
  };

  DeleteProductService = (tenantId: number, productId: number) => {
    this.deleteProductState.inProgress = true;
    const url = `${URLConstants.GetAllProductList}?TenantId=${tenantId}&ProductId=${productId}`;
    return baseService
      .deleteRequest(url)
      .then((response: IApiResponse<IApiSuccessResponse<boolean>>) => {
        if (response.data.Error) {
          this.deleteProductState.error = response.data.Message;
          toast.error(formatMessage(response.data.Message));
        } else this.deleteProductState.success = true;
      })
      .catch((err: string) => {
        this.deleteProductState.error = err;
      })
      .finally(
        action(() => {
          this.deleteProductState.inProgress = false;
        })
      );
  };

  GetAllProductService = (tenantId: number) => {
    this.getAllProductState.inProgress = true;
    const url = `${URLConstants.GetAllProduct}?TenantId=${tenantId}`;
    return baseService
      .getRequest(url)
      .then(
        (response: IApiResponse<IApiSuccessResponse<IProductOptions[]>>) => {
          if (response.data.Error) {
            this.getAllProductState.error = response.data.Message;
            toast.error(formatMessage(response.data.Message));
          } else {
            this.productOptions = response.data.Data;
            this.getAllProductState.success = true;
          }
        }
      )
      .catch((err: string) => {
        this.getAllProductState.error = err;
      })
      .finally(
        action(() => {
          this.getAllProductState.inProgress = false;
        })
      );
  };

  UploadProductFileService = (
    TenantId: number,
    data: any,
    selectedImage: IProductFiles,
    activeIndex: number,
    setFieldValue: any,
    selectedProcessId: number,
    values: any,
    selectedProcessIndex: number,
    setSelectedImageIndex,
    isDimensionTable?: boolean,
    isDefault?: boolean
  ) => {
    this.addProductFileState.inProgress = true;
    const formData = new FormData();
    formData.append('File', data.File);
    let url = URLConstants.AddProductFile + '?TenantId=' + TenantId;
    return baseService
      .postMultipart(url, formData, {
        headers: {
          'Content-Type': 'multipart/form-data',
        },
      })
      .then(
        (
          response: IApiResponse<IApiSuccessResponse<IAddProductFileResponse>>
        ) => {
          if (response.data.Error) {
            this.addProductFileState.error = response.data.Message;
          } else {
            this.addProductFileState.success = true;
            let indexOfTab = (
              values?.Process?.[selectedProcessIndex]?.fileIndices as any[]
            )?.findIndex((itt: any) => itt?.TabIndex === activeIndex);
            // build safe existing FileDetail array
            const existingFiles =
              indexOfTab >= 0 &&
              Array.isArray(values?.Process?.[selectedProcessIndex]?.fileIndices)
                ? values?.Process[selectedProcessIndex]?.fileIndices[indexOfTab]?.FileDetail
                : [];

            const resp = (response?.data?.Data || {}) as any;
            const newFile = {
              ...resp,
              Heading: data?.Heading ?? resp.Heading ?? '',
              FileType: data?.File?.type ?? resp.FileType ?? '',
              FileName: data?.File?.name ?? resp.FileName ?? '',
              // CheckItems: Array.isArray(resp.CheckItems) ? resp.CheckItems : [],
            };
            this.setNewAccidentalDeletedFiles(newFile);

            const targetIdx = indexOfTab >= 0
              ? indexOfTab
              : (values?.Process?.[selectedProcessIndex]?.fileIndices?.length || 0);

            // set new immutable array into Formik
            if (
              existingFiles &&
              Array.isArray(existingFiles) &&
              existingFiles.length > 0
            ) {
              setFieldValue(
                `Process.${selectedProcessIndex}.fileIndices.${targetIdx}.FileDetail`,
                [...existingFiles, { ...newFile, IsNew: true }]
              );
            } else {
              setFieldValue(
                `Process.${selectedProcessIndex}.fileIndices.${targetIdx}.FileDetail`,
                [{ ...newFile, IsNew: true }]
              );
              setFieldValue(
                `Process.${selectedProcessIndex}.fileIndices.${targetIdx}.TabIndex`,
                targetIdx + 1
              );
              setFieldValue(
                `Process.${selectedProcessIndex}.fileIndices.${targetIdx}.CheckItems`,
                []
              );
              setFieldValue(
                `Process.${selectedProcessIndex}.fileIndices.${targetIdx}.IsDimensionTable`,
                isDimensionTable ?? false
              );
              setFieldValue(
                `Process.${selectedProcessIndex}.fileIndices.${targetIdx}.IsNew`,
                true
              );
              setFieldValue(
                `Process.${selectedProcessIndex}.fileIndices.${targetIdx}.IsDefault`,
                isDefault || false
              );
              if (isDefault) {
                setFieldValue('CommonProcess', [
                  ...values.CommonProcess,
                  {
                    CheckItems: [],
                    FileDetail: [{ ...newFile, IsNew: true }],
                    IsNew: true,
                    isDefault: isDefault,
                    IsDimensionTable: false,
                    IsDimension: false,
                    TabIndex: targetIdx + 1,
                  },
                ]);
              }
            }
            setSelectedImageIndex(targetIdx + 1);
          }
        }
      )
      .catch((err: string) => {
        this.addProductFileState.error = err;
      })
      .finally(
        action(() => {
          this.addProductFileState.inProgress = false;
        })
      );
  };

  UploadFileCopyService = (
    TenantId: number,
    data: any,
    heading: string,
    activeIndex: number,
    setFieldValue: any,
    selectedProcessId: number,
    values: any,
    selectedProcessIndex: number,
    setSelectedImageIndex?: (index: number) => void,
    isDimensionTable?: boolean,
    isDefault?: boolean
  ) => {
    this.addProductFileState.inProgress = true;
    let url =
      URLConstants.AddProductCopyFile +
      '?TenantId=' +
      TenantId +
      `&FilePath=${data.FilePath}`;
    return baseService
      .postRequest(url, { TenantId: TenantId, FilePath: data.FilePath })
      .then(
        (
          response: IApiResponse<IApiSuccessResponse<IAddProductFileResponse>>
        ) => {
          if (response.data.Error) {
            this.addProductFileState.error = response.data.Message;
          } else {
            this.addProductFileState.success = true;
            let indexOfTab = (
              values?.Process?.[selectedProcessIndex]?.fileIndices as any[]
            )?.findIndex((itt: any) => itt?.TabIndex === activeIndex);

            const targetIdx = indexOfTab >= 0
              ? indexOfTab
              : (values?.Process?.[selectedProcessIndex]?.fileIndices?.length || 0);

            // build safe existing FileDetail array
            const existingFiles =
              indexOfTab >= 0 &&
              Array.isArray(values?.Process?.[selectedProcessIndex]?.fileIndices)
                ? values?.Process[selectedProcessIndex]?.fileIndices[indexOfTab]?.FileDetail
                : [];

            const resp = (response?.data?.Data || {}) as any;
            const newFile = {
              ...resp,
              FileId: 0,
              Heading: heading ?? resp.Heading ?? '',
              FileType: data?.FileType ?? resp?.FileType ?? '',
              FileName: data?.FileName ?? resp?.FileName ?? '',
              CheckItems: Array.isArray(resp?.CheckItems)
                ? resp.CheckItems
                : [],
            };

            // set new immutable array into Formik
            if (
              existingFiles &&
              Array.isArray(existingFiles) &&
              existingFiles.length > 0
            ) {
              setFieldValue(
                `Process.${selectedProcessIndex}.fileIndices.${targetIdx}.FileDetail`,
                [...existingFiles, { ...newFile, IsNew: true }]
              );
            } else {
              setFieldValue(
                `Process.${selectedProcessIndex}.fileIndices.${targetIdx}.FileDetail`,
                [{ ...newFile, IsNew: true }]
              );
              setFieldValue(
                `Process.${selectedProcessIndex}.fileIndices.${targetIdx}.TabIndex`,
                targetIdx + 1
              );
              setFieldValue(
                `Process.${selectedProcessIndex}.fileIndices.${targetIdx}.CheckItems`,
                []
              );
              setFieldValue(
                `Process.${selectedProcessIndex}.fileIndices.${targetIdx}.IsDimensionTable`,
                isDimensionTable ?? false
              );
              setFieldValue(
                `Process.${selectedProcessIndex}.fileIndices.${targetIdx}.IsNew`,
                true
              );
              setFieldValue(
                `Process.${selectedProcessIndex}.fileIndices.${targetIdx}.IsDefault`,
                isDefault || false
              );
            }
            // Update the selected image index to point to the newly created tab
            if (setSelectedImageIndex) {
              setSelectedImageIndex(targetIdx + 1);
            }
          }
        }
      )
      .catch((err: string) => {
        this.addProductFileState.error = err;
      })
      .finally(
        action(() => {
          this.addProductFileState.inProgress = false;
        })
      );
  };

  DeleteProductFileService = (
    tenantId: number,
    productId: number,
    files1: any[] = [],
    files2: any[] = [],
  ) => {
    this.deleteProductFileState.inProgress = true;
    let url = `${URLConstants.DeleteProductFile}`;

    // Combine both lists to ensure we capture all unique IDs and paths
    const allFiles = [...(Array.isArray(files1) ? files1 : []), ...(Array.isArray(files2) ? files2 : [])];

    const uniqueFiles = mergeUniqueProductFiles(allFiles);

    const uniqueIds = Array.from(new Set(
      uniqueFiles
        .filter(item => item?.FileId)
        .map(item => Number(item.FileId))
    ));

    const deleteFilePaths = Array.from(
      new Set(
        uniqueFiles
          // .filter((item: any) => Number(item?.FileId || 0) <= 0)
          .map((item: any) => item?.FilePath || item?.S3FilePath)
          .filter(Boolean)
      )
    );

    // console.table(
    //   allFiles.map((item: any) => ({
    //     FileId: item?.FileId,
    //     FilePath: item?.FilePath,
    //     S3FilePath: item?.S3FilePath,
    //   }))
    // );

    const payload = {
      FilePath: deleteFilePaths,
      // FileId: uniqueIds,
      TenantId: tenantId,
      ProductId : productId
    };

    // console.log('[STORE] DeleteProductFileService Payload:', payload);

    return baseService
      .deleteArrayRequest(url, payload)
      .then((response: IApiResponse<IApiSuccessResponse<boolean>>) => {
        if (response.data.Error) {
          this.deleteProductFileState.error = response.data.Message;
          toast.error(formatMessage(response.data.Message));
        } else {
          this.deleteProductFileState.success = true;
          runInAction(() => {
            this.newDeletedFiles = [];
            this.oldDeletedFiles = [];
          });
        }
      })
      .catch((err: string) => {
        this.deleteProductFileState.error = err;
      })
      .finally(
        action(() => {
          this.deleteProductFileState.inProgress = false;
        })
      );
  };

  CopyProductService = (data: ICopyProduct) => {
    runInAction(() => {
      this.copyProductState.inProgress = true;
      this.copyProductState.error = '';
      this.copyProductState.success = false;
      this.newCopiedProductId = null;
    });
    const url = URLConstants.CopyProduct;
    return baseService
      .postRequest(url, data)
      .then((response: IApiResponse<IApiSuccessResponse<any>>) => {
        if (response.data.Error) {
          runInAction(() => {
            this.copyProductState.error = response.data.Message;
            this.copyProductState.inProgress = false;
          });
          return;
        }
        // Try to resolve the new product ID directly from the response
        let newId: number | null = null;
        if (response.data.Data && typeof response.data.Data === 'number') {
          newId = response.data.Data;
        } else if (response.data.Data && typeof response.data.Data === 'object' && response.data.Data.Id) {
          newId = Number(response.data.Data.Id);
        }
        if (newId && newId > 0) {
          // ID returned directly — resolve immediately
          runInAction(() => {
            this.newCopiedProductId = newId;
            this.copyProductState.success = true;
            this.copyProductState.inProgress = false;
          });
        } else {
          // Fallback: fetch the product list and find by name
          const tenantId = Number(getTenantIdFromLocalStorage());
          return this.GetAllProductService(tenantId)
            .then(() => {
              const found = this.productOptions.find(
                (p) => String(p.ProductName).trim() === String(data.NewProductId).trim()
              );
              runInAction(() => {
                this.newCopiedProductId = found ? found.Id : null;
                this.copyProductState.success = true;
              });
            })
            .catch((err: string) => {
              runInAction(() => {
                this.copyProductState.error = err;
              });
            })
            .finally(
              action(() => {
                this.copyProductState.inProgress = false;
              })
            );
        }
      })
      .catch((err: string) => {
        runInAction(() => {
          this.copyProductState.error = err;
          this.copyProductState.inProgress = false;
        });
      });
  };

  ValidateProductNameService = (tenantId: number, productId: string): Promise<boolean> => {
    this.ValidateProductNameState.inProgress = true;
    const url = `${URLConstants.ValidateProductName}?TenantId=${tenantId}&&ProductName=${productId}`;
    return baseService
      .getRequest(url)
      .then((response: IApiResponse<IApiSuccessResponse<boolean>>) => {
        if (response.data.Error) {
          this.ValidateProductNameState.error = response.data.Message;
          toast.error(formatMessage(response.data.Message));
          return false;
        } else {
          this.isProductNameValid = response.data.Data;
          this.ValidateProductNameState.success = true;
          return response.data.Data; // Return validation result
        }
      })
      .catch((err: string) => {
        this.ValidateProductNameState.error = err;
        return false; // Return false on error
      })
      .finally(
        action(() => {
          this.ValidateProductNameState.inProgress = false;
        })
      );
  };

  ExportProductReportService = (TenantId: number, filters: string) => {
    this.exportProductReportState.inProgress = true;
    let url = `${URLConstants.ExportProductReport}/${TenantId}?Filters=${filters}`;
    return baseService
      .getRequest(url)
      .then((response: IApiResponse<IApiSuccessResponse<any>>) => {
        if (response.data.Error) {
          this.exportProductReportState.error = response.data.Message;
        } else {
          this.exportProductReportState.success = true;
          this.base64String = { fileString: response.data.Data };
        }
      })
      .catch((err: string) => {
        this.exportProductReportState.error = err;
      })
      .finally(
        action(() => {
          this.exportProductReportState.inProgress = false;
        })
      );
  };

  setCopyProductDetails = (data: IProductVM) => {
    this.product = { ...data };
  };

  setCopiedImagesUrl = (data: IProductFiles[]) => {
    this.copiedImagesUrl = Array.isArray(data) ? [...data] : [];
  };

  setProductId = (data: string) => {
    this.productId = data;
  };

  GetDimensionColumnService = (tenantId: number) => {
    this.getDimensionColumnState.inProgress = true;
    const url = `${URLConstants.GetDimensionTableColumn}/${tenantId}`;
    return baseService
      .getRequest(url)
      .then((response: IApiResponse<IApiSuccessResponse<any>>) => {
        if (response.data.Error) {
          this.getDimensionColumnState.error = response.data.Message;
        } else {
          this.dimensionTableColumnKeys = response.data.Data.DimensionLists;
          this.getDimensionColumnState.success = true;
        }
      })
      .catch((err: string) => {
        this.getDimensionColumnState.error = err;
      })
      .finally(
        action(() => {
          this.getDimensionColumnState.inProgress = false;
        })
      );
  };

  GetDimensionByProcessIdService = (
    ProcessIds: number[],
    setFieldValue: any,
    values: any
  ) => {
    this.getDimensionByProcessIdState.inProgress = true;
    const url = URLConstants.GetDimensionIdByProcess;
    let TenantId = Number(getTenantIdFromLocalStorage() || -1);
    return baseService
      .postRequest(url, { ProcessId: ProcessIds, TenantId })
      .then((response: IApiResponse<IApiSuccessResponse<any>>) => {
        if (response.data.Error) {
          this.getDimensionByProcessIdState.error = response.data.Message;
          toast.error(formatMessage(response.data.Message));
        } else {
          this.processDimension = response.data.Data.DimensionListData;
          this.dimensionOrder = response.data.Data.DimensionOrder || [];
          this.getDimensionByProcessIdState.success = true;
        }
      })
      .catch((err: string) => {
        this.getDimensionByProcessIdState.error = err;
      })
      .finally(
        action(() => {
          this.getDimensionByProcessIdState.inProgress = false;
        })
      );
  };

  setNewDeletedFiles = (data: IProductFiles[]) => {
    // console.log('[STORE] setNewDeletedFiles called with:', data.map(f => f.FileId));
    this.newDeletedFiles = mergeUniqueProductFiles(data);
  };

  setOldDeletedFiles = (data: IProductFiles[]) => {
    // console.log('[STORE] setOldDeletedFiles called with IDs:', data.map(f => f.FileId));
    this.oldDeletedFiles = mergeUniqueProductFiles(data);
    // console.log('[STORE] After deduplication, oldDeletedFiles IDs:', this.oldDeletedFiles.map(f => f.FileId));
  }

  setNewAccidentalDeletedFiles = (data: IProductFiles) => {
    this.newAccidentalDeletedFiles = [...this.newAccidentalDeletedFiles, data];
  };

  resetNewAccidentalDeletedFiles = () => {
    this.newAccidentalDeletedFiles = [];
    this.oldDeletedFiles = [];
    this.newDeletedFiles = [];
  };
  //#endregion

  //#region Computed

  get getNewAccidentalDeletedFiles(): IProductFiles[] {
    return this.newAccidentalDeletedFiles;
  }

  get allProduct(): IProductList[] {
    let processData: IProductList[] = [];
    if (
      this.productList?.ProductList &&
      this.productList.ProductList?.length > 0
    ) {
      this.productList.ProductList.map(
        ({
          Id,
          ProductName,
          TraySize,
          KeyInfo1,
          KeyInfo2,
          KeyInfo3,
          Remarks,
          Process,
          ProductFiles,
          ProductTeplars,
          IsImport,
          Customer,
          Katashiki,
          ProductFilesInfo,
          Dimension,
        }) => {
          processData.push({
            Id,
            ProductName: ProductName || '',
            TraySize: TraySize || '',
            KeyInfo1: KeyInfo1 || '',
            KeyInfo2: KeyInfo2 || '',
            KeyInfo3: KeyInfo3 || '',
            Remarks: Remarks || '',
            Process: Process || [],
            ProductFiles: ProductFiles || [],
            ProductTeplars: ProductTeplars || [],
            IsImport: !!IsImport,
            Customer,
            Katashiki,
            ProductFilesInfo: ProductFilesInfo,
            CommonProcess: [],
            Dimension: {
              SelectedColumns: Dimension?.SelectedColumns || [],
              TableData: Dimension?.TableData || [],
            },
          });
        }
      );
    }
    return processData;
  }

  get productDetails(): IProductVM {
    if (this.product) {
      const {
        Id,
        ProductName,
        TraySize,
        KeyInfo1,
        KeyInfo2,
        KeyInfo3,
        Remarks,
        Process,
        ProductFiles,
        ProductTeplars,
        IsImport,
        Customer,
        Katashiki,
        ProductFilesInfo,
        Dimension,
        CreatedBy,
        UpdatedBy,
        CreatedOn,
        UpdatedOn,
      } = this.product;

      const processTemplates = Process?.length > 0 ? [
        {
          Id: -1,
          ProcessId: -1,
          DueDates: Process.find(item => item.ProcessId === -1)?.DueDates, //Process[0]?.DueDates,
          ProcessOrder: 0,
          ProcessName: 'Tag Allocation',
          Type: -1,
          ShortName: 'ALLOCATE',
          StandardTime: '',
          ViewType: Process[0]?.ViewType,
          fileIndices: [],
          DimensionDetails: {
            SelectedColumns: [],
            TableData: []
          },
        },
        ...Process.filter((p) => p.ProcessId >= 0).map((p) => ({
          ...p,
          StandardTime: p.StandardTime ? p.StandardTime : '',
          DimensionDetails: (() => {
            const rawDimensions =
              p?.fileIndices?.find((d) => d?.IsDimension)?.DimensionsData || [];
            const normalizedDimensions =
              normalizeDimensionsByColumn(rawDimensions);
            const seenColumns = new Set<string>();
            const selectedColumns = normalizedDimensions
              .map((dd) => ({
                id: dd?.DimensionId || -1,
                value: dd?.DimensionId || -1,
                label: dd?.DimensionColumn || '',
                disabled: false,
                data: {
                  Id: dd?.DimensionId || -1,
                  Messages: dd?.DimensionColumn || '',
                  MessagesType: dd?.DimensionType || 0,
                },
              }))
              .filter((col) => {
                const key = `${Number(col?.id)}__${String(col?.label || '')
                  .trim()
                  .toLowerCase()}`;
                if (seenColumns.has(key)) return false;
                seenColumns.add(key);
                return true;
              });

            return {
              SelectedColumns: selectedColumns,
              TableData: transformDimensionData(normalizedDimensions),
            };
          })(),
          fileIndices: p.fileIndices.map(details => ({ ...details, ProductTeplars: ProductTeplars, IsDimensionTable: (details?.DimensionsData?.length > 0) })),

          // FileDetail: ProductFilesInfo?.find(f => f.ProcessId === p.ProcessId)?.fileIndices.map(details => ({ ...details, ProductTeplars: ProductTeplars })) || []
          // FileDetail: ProductFilesInfo?.find(f => f.ProcessId === p.ProcessId)?.fileIndices.map(details => ({ ...details, ProductTeplars: ProductTeplars, IsDimensionTable: !(details?.CheckItems?.length > 0), DimensionTable: [] })) || []
        })),
        {
          Id: -2,
          ProcessId: -2,
          DueDates: Process.find(item => item.ProcessId === -2)?.DueDates,
          ProcessOrder: 0,
          ProcessName: 'Tag Remove',
          Type: -2,
          ShortName: 'REMOVE',
          StandardTime: '',
          ViewType: Process[Process.length - 1]?.ViewType,
          fileIndices: [],
          DimensionDetails: {
            SelectedColumns: [],
            TableData: []
          },
        },
      ] : [];
      // Build Step 3 dimension table.
      // New API shape provides root-level `DimensionsData`; keep old process-level fallback.
      const realProcesses = processTemplates.filter(
        (p: any) => Number(p?.ProcessId) > 0
      );
      const rootDimensionsRaw = Array.isArray((this.product as any)?.DimensionsData)
        ? (this.product as any).DimensionsData
        : [];

      const normalizedRootDimensions = rootDimensionsRaw
        .map((dim: any) => ({
          ...dim,
          DimensionId: Number(dim?.DimensionId || 0),
          DimensionColumn: String(dim?.DimensionColumn || '').trim(),
          DimensionValues: Array.isArray(dim?.DimensionValues)
            ? dim.DimensionValues.map((v: any) => String(v ?? '-'))
            : [],
        }))
        .filter((dim: any) => dim.DimensionId > 0 && dim.DimensionColumn !== '');

      const rootSelectedColumns = normalizedRootDimensions.map((dd: any) => ({
        id: dd?.DimensionId || -1,
        value: dd?.DimensionId || -1,
        label: dd?.DimensionColumn || '',
        disabled: false,
        data: {
          Id: dd?.DimensionId || -1,
          Messages: dd?.DimensionColumn || '',
          MessagesType: dd?.DimensionType || 0,
        },
      }));

      const dedupeColumnsById = (columns: any[] = []) =>
        columns.filter(
          (col: any, index: number, arr: any[]) =>
            index === arr.findIndex((c: any) => Number(c?.id) === Number(col?.id))
        );

      const fallbackSelectedColumns = dedupeColumnsById(
        realProcesses.flatMap((p: any) => p?.DimensionDetails?.SelectedColumns || [])
      );

      const mergedByDimension = new Map<number, any>();
      realProcesses.forEach((process: any) => {
        const dimensions = process?.DimensionDetails?.TableData || [];
        dimensions.forEach((dim: any) => {
          const dimId = Number(dim?.DimensionId);
          if (!dimId) return;

          if (!mergedByDimension.has(dimId)) {
            mergedByDimension.set(dimId, {
              ...dim,
              DimensionValues: [...(dim?.DimensionValues || [])],
            });
            return;
          }

          const existing = mergedByDimension.get(dimId);
          const existingValues = Array.isArray(existing?.DimensionValues)
            ? [...existing.DimensionValues]
            : [];
          const newValues = Array.isArray(dim?.DimensionValues)
            ? dim.DimensionValues
            : [];
          const maxLen = Math.max(existingValues.length, newValues.length);

          existing.DimensionValues = Array.from({ length: maxLen }, (_, idx) => {
            const oldVal = existingValues[idx];
            const newVal = newValues[idx];
            const hasOld =
              oldVal !== undefined &&
              oldVal !== null &&
              String(oldVal).trim() !== '' &&
              String(oldVal).trim() !== '-';
            const hasNew =
              newVal !== undefined &&
              newVal !== null &&
              String(newVal).trim() !== '' &&
              String(newVal).trim() !== '-';
            if (hasOld) return oldVal;
            if (hasNew) return newVal;
            return '-';
          });

          mergedByDimension.set(dimId, existing);
        });
      });

      const hasRootDimensions = normalizedRootDimensions.length > 0;
      const selectedColumns = hasRootDimensions
        ? dedupeColumnsById(rootSelectedColumns)
        : fallbackSelectedColumns;

      const tableData = hasRootDimensions
        ? transformDimensionData(normalizedRootDimensions)
        : transformDimensionData(Array.from(mergedByDimension.values()));

      const defaultDimensionProcess =
        realProcesses.find(
          (p: any) =>
            Array.isArray(p?.DimensionDetails?.SelectedColumns) &&
            p.DimensionDetails.SelectedColumns.length > 0
        ) || realProcesses[0];
      return {
        Id,
        ProductName: ProductName,
        TraySize: TraySize,
        KeyInfo1: KeyInfo1,
        KeyInfo2: KeyInfo2,
        KeyInfo3: KeyInfo3,
        Remarks: Remarks,
        Process: processTemplates,
        ProcessTemplate: '-1',
        ProductFiles: ProductFiles,
        ProductTeplars: ProductTeplars,
        IsImport: !!IsImport,
        ProcessOption: '-1',
        Customer,
        Katashiki,
        ProductFilesInfo: ProductFilesInfo,
        SelectedProcessForDimensionTable:
          defaultDimensionProcess?.ProcessId ??
          (Process.length > 0 ? Process[0].ProcessId : -1),
        CommonProcess: [
          ...(
            (this.product.CommonProcess?.length
              ? this.product.CommonProcess
              : Process.find(p => p.ProcessId > 0)?.fileIndices?.filter(f => f?.IsDefault)?.map(f => ({
                ...f,
                CheckItems: [],
              })))?.map((f, indx) => ({
                ...f,
                TabIndex: indx + 1,
              })) ?? []
          ),
        ],
        Dimension: {
          SelectedColumns: selectedColumns,
          TableData: tableData,
        },
        CreatedBy: CreatedBy,
        UpdatedBy: UpdatedBy,
        CreatedOn: CreatedOn,
        UpdatedOn: UpdatedOn,
        SelectedCheckItemTemplateId: -1,
      };
    }
    return {
      Id: -1,
      ...GetProductState,
    };
  }

  get allProductImages(): IProductImages[] {
    let images = JSON.parse(localStorage.getItem('images')) || [];
    if (this.productImages) {
      if (this.productImages.length > 0) {
        localStorage.setItem(
          'images',
          JSON.stringify([...images, ...this.productImages])
        );
      } else {
        localStorage.removeItem('images');
      }
      images = [...images, ...this.productImages];
    }
    return images;
  }

  get allProductProcessTemplates(): any[] {
    if (this.product) {
      const { Process } = this.product;
      return Process && Process?.length > 0 ? Process : [];

      // [
      //   {
      //     Id: -1,
      //     ProcessId: -1,
      //     DueDates: Process[0].DueDates,
      //     ProcessOrder: 0,
      //     ProcessName: 'Tag Allocation',
      //     Type: 'Special',
      //     ShortName: 'ALLOCATE',
      //   },
      //   ...Process.filter((p) => p.ProcessId >= 0),
      //   {
      //     Id: -2,
      //     ProcessId: -2,
      //     DueDates: Process[Process.length - 1].DueDates,
      //     ProcessOrder: 0,
      //     ProcessName: 'Tag Remove',
      //     Type: 'Special',
      //     ShortName: 'REMOVE',
      //   },
      // ];
    }
    return [];
  }

  get allProductOptions(): IOption[] {
    let productData: IOption[] = [];
    productData.push({
      id: -1,
      value: 'please_select',
      disabled: true,
    });
    if (this.productOptions && this.productOptions?.length > 0) {
      this.productOptions.map((product) => {
        productData.push({
          id: product.Id,
          value: product.ProductName,
        });
      });
    }
    return productData;
  }

  get allProductReactSelectOptions(): IReactSelectOption[] {
    let productData: IReactSelectOption[] = [];
    // productData.push({
    //   value: "-1",
    //   label: formatMessage('please_select'),
    //   isDisabled: true,
    // });
    if (this.productOptions && this.productOptions?.length > 0) {
      this.productOptions.map((product) => {
        productData.push({
          value: String(product.Id),
          label: product.ProductName,
        });
      });
    }
    return productData;
  }

  get allProductSearchSelectOptions() {
    let productData = [];
    productData.push({
      Id: -1,
      Name: formatMessage('please_select'),
      isDisabled: true,
    });
    
    if (this.productOptions && this.productOptions?.length > 0) {
      this.productOptions.map((product) => {
        productData.push({
          Id: Number(product.Id),
          Name: product.ProductName,
        });
      });
    }
    return productData;
  }

  get getCopiedImagesUrl(): Array<IProductFiles> {
    return this.copiedImagesUrl;
  }

  get getproductId(): string {
    return this.productId;
  }

  get getDimensionTableColumnKeys(): IGetDimensionColumnResponse[] {
    return this.dimensionTableColumnKeys ?? [];
  }

  get getDimensionByProcessId(): any {
    return this.processDimension;
  }

  get getDimensionOrder(): number[] {
    return this.dimensionOrder ?? [];
  }
  //#endregion

  //#region Reset
  reset = () => {
    this.productList = GetAllProducts;
  };

  resetProductDetail = () => {
    this.product = undefined;
    this.productState = { ...this.initialStateValue };
  };

  resetDeleteProduct = () => {
    this.deleteProductState = { ...this.initialStateValue };
  };

  resetAddUpdateProductState = () => {
    this.addUpdateProductState.inProgress = false;
    this.addUpdateProductState.success = false;
    this.addUpdateProductState.error = '';
  };

  resetAddProductFileState = () => {
    this.addProductFileState = { ...this.initialStateValue };
  };

  resetDeleteProductFileState = () => {
    this.deleteProductFileState = { ...this.initialStateValue };
  };

  resetProductImages = () => {
    this.productImages = undefined;
  };

  resetCopyProductState = () => {
    this.copyProductState = { ...this.initialStateValue };
    this.newCopiedProductId = null;
  };

  resetValidateProductNameState = () => {
    this.ValidateProductNameState = { ...this.initialStateValue };
    this.isProductNameValid = false;
  };

  resetCopiedImagesUrl = () => {
    this.copiedImagesUrl = [];
  };

  setIsProductNameValid = (val: boolean) => {
    this.isProductNameValid = val;
  };

  resetExportProductReportState = () => {
    this.exportProductReportState = { ...this.initialStateValue };
    // this.base64String = undefined;
  }

  //#endregion
}

export default new ProductStore();
